import type { definitions } from '@luxuryescapes/contract-trip'

import type { PublicAccommodationPricing } from 'tripPlanner/utils/accommodationPricingDisplay'
import type {
  EndDateTimeMoment,
  EndDateTimeMomentWithTimeZone,
  GeoPlace,
  Place,
  StartDateTimeMoment,
  StartDateTimeMomentWithTimeZone,
} from './placeAndDateTime'

// type FareClass = typeof fareClasses[number]

export const parentItemTypes = [
  'LE_BOOKINGS',
  'ACCOMMODATION',
  'FLIGHT',
  'TOUR_AND_CRUISES',
  'TOURS',
  'CRUISES',
  'THINGS_TO_DO',
  'TRANSPORT',
  'CAR',
  'NOTE',
] as const

export type ItemLabel = 'Experience' | 'Hotel' | 'Tour' | 'Flight' | 'Cruise'

export type ParentItemType = (typeof parentItemTypes)[number]

export const toursAndCruisesTypes = ['TOUR', 'CRUISE'] as const

export const transportTypes = [
  'TRAIN',
  'FERRY',
  'BUS',
  'OTHER_TRANSPORT',
] as const

export const nonSimpleMoveItemTypes = [
  'FLIGHT',
  ...transportTypes,
  'CAR_RENTAL',
] as const

export const thingsToDoTypes = [
  'RESTAURANT_BAR',
  'ATTRACTION',
  'EVENT',
  'THING_TO_DO',
] as const

export type ThingsToDoItemType = (typeof thingsToDoTypes)[number]

export const itemTypes = [
  'ACCOMMODATION',
  ...toursAndCruisesTypes,
  ...transportTypes,
  ...thingsToDoTypes,
  'EXPERIENCE',
  'CAR_RENTAL',
  'FLIGHT',
  'NOTE',
] as const

export type AccommodationType = 'ACCOMMODATION'
export type TransportType = (typeof transportTypes)[number]
export type NonSimpleMoveItemType = (typeof nonSimpleMoveItemTypes)[number]
export type ItemType = (typeof itemTypes)[number]

export type PinnableItemType = Exclude<
  ItemType,
  'NOTE' | 'SEARCH_FLIGHTS' | 'SEARCH_ACCOMMODATION'
>

interface BaseTripItem {
  id: string
  tripId: string
  notes?: string
  createdAt: moment.Moment
  updatedAt: moment.Moment
  userUpdatedAt?: moment.Moment
  createdBy: string
  updatedBy: string
  sortableName: string
  isInItinerary?: boolean
  summaryRankString: string
  itineraryRankString: string
  isSuggested: boolean
  isFromEmail: boolean
  isAiGenerated: boolean
  insiderTip?: string
  alternativeGroup?: string
  travelPreferences?: string
  fromHotelTravelPreferences?: string
  suggestReplanDay?: boolean
  isHotelInclusion?: boolean
}

export interface Bookable {
  isBooked: boolean
}

export interface StartEndDays {
  startDay?: number
  endDay?: number
}

export type SavedItemData =
  | SavedExperienceData
  | definitions['savedFlightSearchData']
  | SavedHotelData
  | definitions['savedTourData']
  | definitions['savedCruiseData']

export interface NoteItem extends
  BaseTripItem,
  StartDateTimeMoment,
  EndDateTimeMoment,
  StartEndDays
{
  type: 'NOTE'
  title?: string
  sourceType: 'NA'
  /** @deprecated */
  link?: string
  isSectionHeader?: boolean
  notes: string
}

export interface AttractionItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'ATTRACTION'
  name?: string
  place: Place
  sourceType: 'NA' | 'Explore'
  phone?: string
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
  /** @deprecated Use imageIds instead */
  imageId?: string
  imageIds?: Array<string>
}

export interface CarRentalItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'CAR_RENTAL'
  sourceType: 'NA'
  companyName?: string
  phone?: string
  startPlace: Place
  endPlace?: Place
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
  carType?: string
}

export interface CruiseItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'CRUISE'
  name: string
  sourceType: 'NA' | 'LE_Cruise'
  startPlace: Place
  endPlace?: Place
  phone?: string
  url?: string
  code?: string
  savedItemData?: SavedCruiseData | SavedTourData
  confirmationUrl?: string
  confirmationCode?: string
  price?: number
  operatorName?: string
}

export interface ExperienceItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'EXPERIENCE'
  name: string
  sourceType: 'LE_Experience'
  place: Place
  phone?: string
  code?: string
  url?: string
  savedItemData?: SavedExperienceData // LE Experience Orders wont necessarily have this defined
  confirmationUrl?: string
  confirmationCode?: string
}

export interface EventItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'EVENT'
  name?: string
  place: Place
  sourceType: 'NA'
  phone?: string
  code?: string
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
}

export interface FlightItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'FLIGHT'
  sourceType: 'NA' | 'LE_Flight'
  originAirportCode: string
  destinationAirportCode: string
  startPlace: Place
  endPlace: Place
  airlineName?: string
  flightNumber?: string
  // fareClass: FareClass
  savedItemData?: SavedFlightSearchData
  url?: string
  code?: string
  price?: number
  confirmationCode?: string
  confirmationItemCodes: Array<string>
  confirmationUrl?: string
}

export interface AccommodationItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'ACCOMMODATION'
  sourceType: 'NA' | 'LE_Hotel' | 'LE_Hotel_Bedbank' | 'LE_Accommodation_Rental'
  place: Place
  phone?: string
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
  savedItemData?: SavedAccommodationData
  code?: string
  subCode?: string
}

export interface RestaurantBarItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'RESTAURANT_BAR'
  name?: string
  place: Place
  sourceType: 'NA' | 'Explore'
  phone?: string
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
}

export interface BaseTransportItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  startPlace: Place
  endPlace: Place
  sourceType: 'NA'
  phone?: string
  operatorName?: string
  seat?: string
  price?: number
  url?: string
  confirmationUrl?: string
  confirmationCode?: string
}

export interface BusItem extends BaseTransportItem {
  type: 'BUS'
}

export interface FerryItem extends BaseTransportItem {
  type: 'FERRY'
}

export interface OtherTransportItem extends BaseTransportItem {
  type: 'OTHER_TRANSPORT'
}

export interface TrainItem extends BaseTransportItem {
  type: 'TRAIN'
}

export type TransportItem = BusItem | FerryItem | OtherTransportItem | TrainItem

export interface ThingToDoItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'THING_TO_DO'
  name?: string
  sourceType: 'NA'
  startPlace: Place
  endPlace: Place
  phone?: string
  code?: string
  url?: string
  price?: number
  confirmationUrl?: string
  confirmationCode?: string
}

export interface TourItem extends
  BaseTripItem,
  Bookable,
  StartDateTimeMomentWithTimeZone,
  EndDateTimeMomentWithTimeZone,
  StartEndDays
{
  type: 'TOUR'
  sourceType: 'NA' | 'LE_Tour'
  name: string
  startPlace: Place
  endPlace?: Place
  phone?: string
  url?: string
  confirmationUrl?: string
  confirmationCode?: string
  savedItemData?: SavedTourData
  price?: number
  operatorName?: string
  code?: string
}

export type MapItem = Exclude<TripItem, NoteItem>

export type MarkerItem = MapItem &
  (
    | {
      type:
        | 'EVENT'
        | 'EXPERIENCE'
        | 'ACCOMMODATION'
        | 'RESTAURANT_BAR'
        | 'ATTRACTION'
      place: GeoPlace
    }
    | {
      type: 'CAR_RENTAL' | 'CRUISE' | 'TOUR' | 'THING_TO_DO'
      startPlace: GeoPlace
    }
  )

export type RouteItem = MapItem & {
  type: 'FLIGHT' | 'BUS' | 'FERRY' | 'TRAIN' | 'OTHER_TRANSPORT'
  startPlace: GeoPlace
  endPlace: GeoPlace
}

export type TripItem =
  | AccommodationItem
  | AttractionItem
  | CarRentalItem
  | CruiseItem
  | EventItem
  | ExperienceItem
  | FlightItem
  | RestaurantBarItem
  | ThingToDoItem
  | TourItem
  | NoteItem
  | TransportItem

export type LEFlightItem = Utils.RequiredProp<FlightItem, 'savedItemData'>

export type CustomItem = TripItem & { sourceType: 'NA' | 'Explore' }

export type LETripItem =
  | Utils.RequiredProp<CruiseItem, 'savedItemData'>
  | Utils.RequiredProp<ExperienceItem, 'savedItemData'>
  | LEFlightItem
  | Utils.RequiredProp<TourItem, 'savedItemData'>
  | Utils.RequiredProp<AccommodationItem, 'savedItemData'>

export type SavedAccommodationRentalData =
  definitions['savedAccommodationRentalData']
export type SavedHotelData = definitions['savedHotelData'] & {
  rateId?: string
  pricing?: PublicAccommodationPricing
}
export type HotelSavedBooking = Pick<SavedHotelData, 'checkIn' | 'checkOut' | 'occupancies' | 'packageId' | 'roomTypeId' | 'rateId'> & {
  /** Charter bundle (Maldives direct): saved origin airport code. */
  charterOriginAirportCode?: string
  /** Charter bundle (Maldives direct): saved flight fare class. */
  charterFareClass?: string
}
export type SavedAccommodationData = SavedHotelData | SavedAccommodationRentalData
export type SavedTourData = definitions['savedTourData']
export type SavedExperienceData = definitions['savedExperienceData']
export type SavedFlightSearchData = definitions['savedFlightSearchData']
export type SavedCruiseData =
  | definitions['savedCruiseData']
  | definitions['savedTourData']

export type BookmarkDatesAndOccupancy = {
  checkInDate?: moment.Moment
  checkOutDate?: moment.Moment
  occupancies?: Array<App.Occupants>
  durationInDays?: number
}
